import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { TenantTheme } from '@up2d8/contracts';
import { useSession } from './session';
import { applyTenantTheme } from './theme';

/**
 * Haalt het thema van het eigen bedrijf op en zet het op `<html>` (Stap 43).
 *
 * Geeft het thema ook terug, zodat een schil het logo en de naam van de klant kan tonen (Extra 1).
 *
 * In één hook en niet per scherm: de kleuren horen bij de sessie en niet bij een pagina, en een
 * scherm dat ze zelf zou moeten toepassen, is een scherm dat het ooit vergeet.
 *
 * Een uur gecachet. Een huisstijl wijzigt een paar keer per jaar; elke paginawissel opnieuw vragen
 * is verkeer voor niets.
 */
export function useTenantTheme(): TenantTheme | null {
  const { api, has } = useSession();

  const theme = useQuery({
    queryKey: ['tenant-theme'],
    enabled: has('tenants.tenant.view'),
    staleTime: 60 * 60 * 1000,
    retry: false,
    queryFn: async (): Promise<TenantTheme | null> => {
      const { data, error } = await api.GET('/api/v1/tenant/theme');
      if (error || !data) return null;
      return data as TenantTheme;
    },
  });

  useEffect(() => {
    if (theme.data) applyTenantTheme(theme.data);
  }, [theme.data]);

  // Sinds Extra 1 zit de naam en het logo in hetzelfde antwoord, en de schil heeft ze nodig. Dat
  // teruggeven in plaats van een tweede hook: het is dezelfde query, en twee hooks met dezelfde
  // sleutel zijn twee plekken die ooit uiteenlopen.
  return theme.data ?? null;
}
